import React, { useState, useEffect, useRef } from "react";
import { Button, PopupModal } from "../../shared";
import {
  useAllegroInitializeAuth,
  useAllegroExchangeToken,
} from "../../hooks/allegro";
import { queryClient } from "../../App";
import "./AllegroConnectionModal.scss";

interface AllegroConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AllegroConnectionModal: React.FC<AllegroConnectionModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [authFlow, setAuthFlow] = useState<any>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { mutate: initializeAuth, isPending: isInitializing } =
    useAllegroInitializeAuth();
  const { mutate: exchangeToken, isPending: isExchanging } =
    useAllegroExchangeToken();

  const clearPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const startAuthFlow = () => {
    setError("");
    setSuccess(false);

    initializeAuth([], {
      onSuccess: (data) => {
        setAuthFlow(data.data);
        setIsConnecting(true);

        // Start polling for token exchange
        intervalRef.current = setInterval(() => {
          if (data.data?.deviceCode) {
            exchangeToken(
              { deviceCode: data.data.deviceCode },
              {
                onSuccess: () => {
                  setSuccess(true);
                  setIsConnecting(false);
                  clearPolling();

                  // Refresh auth status
                  queryClient.invalidateQueries({
                    queryKey: ["allegro-auth-status"],
                  });

                  // Auto close after success
                  setTimeout(() => {
                    onClose();
                  }, 2000);
                },
                onError: (error: any) => {
                  // Don't show error for polling - authorization might still be pending
                  console.log("Polling for authorization...");
                },
              }
            );
          }
        }, 2000); // Poll every 2 seconds
      },
      onError: (error: any) => {
        setError(
          error?.data?.message ||
            "Nie udało się zainicjować autoryzacji Allegro"
        );
        setIsConnecting(false);
      },
    });
  };

  const handleManualExchange = () => {
    if (authFlow?.deviceCode) {
      exchangeToken(
        { deviceCode: authFlow.deviceCode },
        {
          onSuccess: () => {
            setSuccess(true);
            setIsConnecting(false);
            clearPolling();

            // Refresh auth status
            queryClient.invalidateQueries({
              queryKey: ["allegro-auth-status"],
            });

            // Auto close after success
            setTimeout(() => {
              onClose();
            }, 2000);
          },
          onError: (error: any) => {
            setError(
              error?.data?.message ||
                "Autoryzacja jeszcze nie została ukończona"
            );
          },
        }
      );
    }
  };

  const handleClose = () => {
    clearPolling();
    setAuthFlow(null);
    setIsConnecting(false);
    setError("");
    setSuccess(false);
    onClose();
  };

  const openVerificationUrl = () => {
    if (authFlow?.verificationUriComplete) {
      window.open(authFlow.verificationUriComplete, "_blank");
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, []);

  // Clear polling when modal closes
  useEffect(() => {
    if (!isOpen) {
      clearPolling();
    }
  }, [isOpen]);

  return (
    <PopupModal isOpen={isOpen} onClose={handleClose}>
      <div className="allegro-connection-modal">
        <div className="allegro-connection-modal__header">
          <h2 className="popup-modal__header">Połączenie z Allegro</h2>
        </div>

        <div className="allegro-connection-modal__content">
          {!authFlow && !success && (
            <div className="allegro-connection-modal__initial">
              <p>
                Aby połączyć się z Allegro, należy autoryzować aplikację w
                serwisie Allegro.
              </p>
              <Button
                label={
                  isInitializing ? "Inicjowanie..." : "Rozpocznij autoryzację"
                }
                styleType="primary"
                onClick={startAuthFlow}
                disabled={isInitializing}
              />
            </div>
          )}

          {authFlow && !success && (
            <div className="allegro-connection-modal__auth-flow">
              <div className="allegro-connection-modal__instructions">
                <p>
                  <strong>Krok 1:</strong> Kliknij poniższy przycisk aby
                  otworzyć stronę autoryzacji Allegro
                </p>
                <p>
                  <strong>Krok 2:</strong> Zaloguj się na swoje konto Allegro i
                  autoryzuj aplikację
                </p>
                <p>
                  <strong>Krok 3:</strong> Wróć tutaj - połączenie zostanie
                  automatycznie wykryte
                </p>
              </div>

              <div className="allegro-connection-modal__verification">
                <p className="allegro-connection-modal__code">
                  Kod weryfikacyjny: <strong>{authFlow.userCode}</strong>
                </p>

                <div className="allegro-connection-modal__buttons">
                  <Button
                    label="Otwórz stronę autoryzacji"
                    styleType="primary"
                    onClick={openVerificationUrl}
                  />

                  <Button
                    label={
                      isExchanging ? "Sprawdzanie..." : "Sprawdź połączenie"
                    }
                    styleType="stroke"
                    onClick={handleManualExchange}
                    disabled={isExchanging}
                  />
                </div>

                {isConnecting && (
                  <div className="allegro-connection-modal__status">
                    <div className="allegro-connection-modal__spinner"></div>
                    <p>
                      Oczekiwanie na autoryzację... (automatyczne sprawdzanie co
                      2 sekundy)
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {success && (
            <div className="allegro-connection-modal__success">
              <div className="allegro-connection-modal__success-icon">✓</div>
              <h3>Połączenie z Allegro zostało nawiązane!</h3>
              <p>Okno zostanie zamknięte automatycznie.</p>
            </div>
          )}

          {error && (
            <div className="allegro-connection-modal__error">
              <p>{error}</p>
              <Button
                label="Spróbuj ponownie"
                styleType="primary"
                onClick={startAuthFlow}
              />
            </div>
          )}
        </div>

        <div className="popup-modal__buttons">
          <Button
            label="Zamknij"
            styleType="stroke"
            onClick={handleClose}
            type="button"
          />
        </div>
      </div>
    </PopupModal>
  );
};

export default AllegroConnectionModal;
